/** About 页面内容管理：结构化表单、全局富文本编辑器、草稿保存和发布。 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { toast } from '../components/toast.js';
import { API_BASE_URL, uploadAboutImage, websiteApi } from '../api.js';
import '../components/rich-editor.js';

interface AboutContent {
  title: string;
  seo_title: string;
  seo_description: string;
  intro: Array<{ type: string; content: string }>;
  story: Array<{ type: string; content: string }>;
}

const emptyContent = (): AboutContent => ({
  title: '',
  seo_title: '',
  seo_description: '',
  intro: [{ type: 'rich_text', content: '' }],
  story: [{ type: 'rich_text', content: '' }],
});

function firstRichText(value: unknown): string {
  if (!Array.isArray(value)) return '';
  return value
    .filter((item): item is { content?: unknown } => Boolean(item && typeof item === 'object'))
    .map(item => String(item.content || ''))
    .join('');
}

@customElement('crm-about-page')
export class CrmAboutPage extends BaseElement {
  @state() private form = emptyContent();
  @state() private loading = false;
  @state() private saving = false;

  static styles = css``;

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    try {
      const result = await websiteApi.aboutAdmin();
      const draft = result.draft as Record<string, unknown>;
      this.form = {
        title: String(draft.title || ''),
        seo_title: String(draft.seo_title || ''),
        seo_description: String(draft.seo_description || ''),
        intro: [{ type: 'rich_text', content: firstRichText(draft.intro) }],
        story: [{ type: 'rich_text', content: firstRichText(draft.story) }],
      };
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载 About 内容失败');
    } finally {
      this.loading = false;
    }
  }

  private buildPayload(): Record<string, unknown> {
    return {
      title: this.form.title.trim(),
      seo_title: this.form.seo_title.trim(),
      seo_description: this.form.seo_description.trim(),
      intro: this.form.intro,
      story: this.form.story,
    };
  }

  private async save(publish: boolean) {
    this.saving = true;
    try {
      const payload = this.buildPayload();
      if (publish) {
        const result = await websiteApi.publishAbout(payload);
        toast.success('About 内容已发布');
        this.applyContent(result.content);
      } else {
        const result = await websiteApi.saveAboutDraft(payload);
        toast.success('草稿已保存');
        this.applyContent(result.draft);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : (publish ? '发布失败' : '保存失败'));
    } finally {
      this.saving = false;
    }
  }

  private applyContent(value: Record<string, unknown>) {
    this.form = {
      title: String(value.title || ''),
      seo_title: String(value.seo_title || ''),
      seo_description: String(value.seo_description || ''),
      intro: [{ type: 'rich_text', content: firstRichText(value.intro) }],
      story: [{ type: 'rich_text', content: firstRichText(value.story) }],
    };
  }

  private updateField(field: 'title' | 'seo_title' | 'seo_description', event: Event) {
    this.form = { ...this.form, [field]: (event.target as HTMLInputElement).value };
  }

  private updateRichText(section: 'intro' | 'story', event: CustomEvent) {
    this.form = { ...this.form, [section]: [{ type: 'rich_text', content: String(event.detail.value || '') }] };
  }

  private imageUploader = async (file: File) => {
    const result = await uploadAboutImage(file);
    return { cid: '', displayUrl: `${API_BASE_URL}${result.url}` };
  };

  render() {
    return html`
      <crm-loading-overlay ?active=${this.loading}></crm-loading-overlay>
      <div class="mb-4 flex items-center justify-between gap-4">
        <div><h1 class="text-xl font-semibold text-text">About 页面管理</h1><p class="text-sm text-text-muted mt-1">使用表单编辑内容，发布后由 Website 构建脚本读取标准 JSON。</p></div>
        <div class="flex gap-2 shrink-0">
          <button class="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-bg" @click=${() => this.save(false)} ?disabled=${this.saving || this.loading}>保存草稿</button>
          <button class="px-3 py-1.5 text-sm text-white bg-primary rounded-md hover:bg-primary-hover" @click=${() => this.save(true)} ?disabled=${this.saving || this.loading}>发布</button>
        </div>
      </div>

      <div class="space-y-4">
        <section class="bg-surface border border-border rounded-md p-5 space-y-4">
          <h2 class="text-base font-semibold text-text">页面信息</h2>
          <label class="block text-sm text-text-secondary">页面标题<input class="mt-1 w-full px-3 py-2 border border-border rounded-md bg-bg text-text" .value=${this.form.title} @input=${(e: Event) => this.updateField('title', e)} maxlength="200"></label>
          <label class="block text-sm text-text-secondary">SEO 标题<input class="mt-1 w-full px-3 py-2 border border-border rounded-md bg-bg text-text" .value=${this.form.seo_title} @input=${(e: Event) => this.updateField('seo_title', e)} maxlength="300"></label>
          <label class="block text-sm text-text-secondary">SEO 描述<textarea class="mt-1 w-full px-3 py-2 border border-border rounded-md bg-bg text-text" rows="3" .value=${this.form.seo_description} @input=${(e: Event) => this.updateField('seo_description', e)} maxlength="500"></textarea></label>
        </section>

        ${this.renderEditorSection('Intro 内容', 'intro', this.form.intro[0]?.content || '')}
        ${this.renderEditorSection('Story 内容', 'story', this.form.story[0]?.content || '')}

      </div>
    `;
  }

  private renderEditorSection(title: string, section: 'intro' | 'story', value: string) {
    return html`<section class="bg-surface border border-border rounded-md p-5"><h2 class="text-base font-semibold text-text mb-3">${title}</h2><crm-rich-editor .value=${value} .customImageUploader=${this.imageUploader} placeholder="编辑内容..." @input=${(e: CustomEvent) => this.updateRichText(section, e)}></crm-rich-editor></section>`;
  }
}
